import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { Shield, Plus, AlertTriangle, CheckCircle, Clock, FileText, Users, HardHat, Search, Filter, Eye, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SafetyCompliance = () => {
  const [activeTab, setActiveTab] = useState("incidents");
  const [incidents, setIncidents] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [ppeRecords, setPpeRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [stats, setStats] = useState(null);

  // Incident Dialog
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0,5),
    incident_type: "Near Miss",
    severity: "Low",
    location: "",
    description: "",
    persons_involved: "",
    injuries: "",
    immediate_action: "",
    root_cause: "",
    corrective_action: "",
    status: "Open"
  });

  // Checklist Dialog
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistForm, setChecklistForm] = useState({
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    checklist_type: "Daily Safety",
    items: [
      { item: "PPE compliance checked", status: "Yes", remarks: "" },
      { item: "Housekeeping satisfactory", status: "Yes", remarks: "" },
      { item: "Fire extinguishers accessible", status: "Yes", remarks: "" },
      { item: "First aid kit available", status: "Yes", remarks: "" },
      { item: "Signages in place", status: "Yes", remarks: "" },
      { item: "Excavation properly barricaded", status: "NA", remarks: "" },
      { item: "Electrical safety ensured", status: "Yes", remarks: "" },
      { item: "Vehicle movement controlled", status: "Yes", remarks: "" }
    ],
    overall_remarks: ""
  });

  // PPE Dialog
  const [ppeDialogOpen, setPpeDialogOpen] = useState(false);
  const [ppeForm, setPpeForm] = useState({
    employee_id: "",
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    helmet: true,
    safety_shoes: true,
    reflective_jacket: true,
    gloves: false,
    safety_glasses: false,
    ear_plugs: false,
    harness: false,
    remarks: ""
  });

  const INCIDENT_TYPES = ["Near Miss", "First Aid", "Minor Injury", "Major Injury", "Fatality", "Property Damage", "Environmental", "Fire", "Other"];
  const SEVERITY_LEVELS = ["Low", "Medium", "High", "Critical"];
  const CHECKLIST_TYPES = ["Daily Safety", "Weekly Safety", "Monthly Audit", "Pre-Work", "Hot Work Permit", "Confined Space", "Height Work"];

  const currentUser = getUser() || {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProjects();
    fetchEmployees();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedProject || projects.length > 0) {
      fetchData();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/employees');
      setEmployees(response.data.filter(e => e.status === 'Active'));
    } catch (error) {
      console.error("Failed to fetch employees");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incRes, checkRes, ppeRes, statsRes] = await Promise.all([
        apiClient.get(`/safety/incidents?project_id=${selectedProject}`),
        apiClient.get(`/safety/checklists?project_id=${selectedProject}`),
        apiClient.get(`/safety/ppe-records?project_id=${selectedProject}`),
        apiClient.get(`/safety/stats?project_id=${selectedProject}`)
      ]);
      setIncidents(incRes.data);
      setChecklists(checkRes.data);
      setPpeRecords(ppeRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch safety data");
    } finally {
      setLoading(false);
    }
  };

  const handleIncidentSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/safety/incidents', { ...incidentForm, project_id: selectedProject });
      toast.success("Incident reported");
      setIncidentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to report incident");
    }
  };

  const handleChecklistSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/safety/checklists', { ...checklistForm, project_id: selectedProject });
      toast.success("Checklist submitted");
      setChecklistDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit checklist");
    }
  };

  const handlePpeSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/safety/ppe-records', { ...ppeForm, project_id: selectedProject });
      toast.success("PPE record saved");
      setPpeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save PPE record");
    }
  };

  const updateChecklistItem = (index, field, value) => {
    setChecklistForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      return { ...prev, items };
    });
  };

  const closeIncident = async (incidentId) => {
    try {
      await apiClient.put(`/safety/incidents/${incidentId}/close`);
      toast.success("Incident closed");
      fetchData();
    } catch (error) {
      toast.error("Failed to close incident");
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      "Low": "bg-success/20 text-success",
      "Medium": "bg-warning/20 text-warning",
      "High": "bg-orange-100 text-orange-600",
      "Critical": "bg-error/20 text-error"
    };
    return colors[severity] || colors["Low"];
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">SAFETY & COMPLIANCE</h1>
          <p className="text-slate-600 mt-2 font-body">Incident reports, safety checklists, and PPE tracking</p>
        </div>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm"
        >
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card border-l-4 border-l-error">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Open Incidents</p>
          <p className="font-heading font-black text-3xl text-error">{stats?.open_incidents || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Days Without Incident</p>
          <p className="font-heading font-black text-3xl text-success">{stats?.days_without_incident || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Checklists This Month</p>
          <p className="font-heading font-black text-3xl text-primary">{stats?.checklists_this_month || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">PPE Compliance</p>
          <p className="font-heading font-black text-3xl text-accent">{stats?.ppe_compliance || 0}%</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        {[
          { id: "incidents", label: "Incidents", icon: AlertTriangle },
          { id: "checklists", label: "Safety Checklists", icon: FileText },
          { id: "ppe", label: "PPE Tracking", icon: HardHat }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-4 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id ? 'border-b-2 border-primary text-primary' : 'text-slate-600'}`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Incidents Tab */}
      {activeTab === "incidents" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setIncidentDialogOpen(true)} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Report Incident</span>
            </button>
          </div>
          <div className="space-y-3">
            {incidents.length > 0 ? incidents.map(incident => (
              <div key={incident.id} className={`stat-card border-l-4 ${incident.status === 'Open' ? 'border-l-error' : 'border-l-success'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getSeverityColor(incident.severity)}`}>{incident.severity}</span>
                      <span className="text-sm font-medium">{incident.incident_type}</span>
                      <span className={`px-2 py-1 rounded-sm text-xs ${incident.status === 'Open' ? 'bg-error/20 text-error' : 'bg-success/20 text-success'}`}>{incident.status}</span>
                    </div>
                    <p className="text-sm text-slate-700">{incident.description}</p>
                    <p className="text-xs text-slate-500 mt-1">{incident.date} at {incident.time} | {incident.location}</p>
                  </div>
                  {incident.status === 'Open' && (
                    <button onClick={() => closeIncident(incident.id)} className="btn-outline text-xs">Close Incident</button>
                  )}
                </div>
              </div>
            )) : (
              <div className="text-center py-12 stat-card">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-success" />
                <h3 className="font-heading font-bold text-xl mb-2">No Incidents Reported</h3>
                <p className="text-slate-600">Keep up the good safety record!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checklists Tab */}
      {activeTab === "checklists" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setChecklistDialogOpen(true)} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>New Checklist</span>
            </button>
          </div>
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Type</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Items Checked</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Compliance</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Checked By</th>
                </tr>
              </thead>
              <tbody>
                {checklists.map(cl => {
                  const yesCount = cl.items?.filter(i => i.status === 'Yes').length || 0;
                  const totalCount = cl.items?.filter(i => i.status !== 'NA').length || 1;
                  const compliance = Math.round((yesCount / totalCount) * 100);
                  return (
                    <tr key={cl.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono text-sm">{cl.date}</td>
                      <td className="py-3 px-4 text-sm">{cl.checklist_type}</td>
                      <td className="py-3 px-4 text-center font-mono">{cl.items?.length || 0}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-sm text-xs font-bold ${compliance >= 80 ? 'bg-success/20 text-success' : compliance >= 60 ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>
                          {compliance}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">{cl.checked_by}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PPE Tab */}
      {activeTab === "ppe" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setPpeDialogOpen(true)} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Record PPE Check</span>
            </button>
          </div>
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Employee</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Helmet</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Shoes</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Jacket</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Gloves</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Glasses</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {ppeRecords.map(ppe => (
                  <tr key={ppe.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono text-sm">{ppe.date}</td>
                    <td className="py-3 px-4 text-sm font-medium">{ppe.employee_name}</td>
                    <td className="py-3 px-4 text-center">{ppe.helmet ? <CheckCircle className="w-4 h-4 text-success mx-auto" /> : <AlertTriangle className="w-4 h-4 text-error mx-auto" />}</td>
                    <td className="py-3 px-4 text-center">{ppe.safety_shoes ? <CheckCircle className="w-4 h-4 text-success mx-auto" /> : <AlertTriangle className="w-4 h-4 text-error mx-auto" />}</td>
                    <td className="py-3 px-4 text-center">{ppe.reflective_jacket ? <CheckCircle className="w-4 h-4 text-success mx-auto" /> : <AlertTriangle className="w-4 h-4 text-error mx-auto" />}</td>
                    <td className="py-3 px-4 text-center">{ppe.gloves ? <CheckCircle className="w-4 h-4 text-success mx-auto" /> : <span className="text-slate-300">-</span>}</td>
                    <td className="py-3 px-4 text-center">{ppe.safety_glasses ? <CheckCircle className="w-4 h-4 text-success mx-auto" /> : <span className="text-slate-300">-</span>}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{ppe.remarks || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Incident Dialog */}
      <Dialog open={incidentDialogOpen} onOpenChange={setIncidentDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">REPORT INCIDENT</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIncidentSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Date *</label>
                <input type="date" required value={incidentForm.date} onChange={(e) => setIncidentForm({...incidentForm, date: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Time *</label>
                <input type="time" required value={incidentForm.time} onChange={(e) => setIncidentForm({...incidentForm, time: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Severity *</label>
                <select required value={incidentForm.severity} onChange={(e) => setIncidentForm({...incidentForm, severity: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  {SEVERITY_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Incident Type *</label>
                <select required value={incidentForm.incident_type} onChange={(e) => setIncidentForm({...incidentForm, incident_type: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Location *</label>
                <input type="text" required value={incidentForm.location} onChange={(e) => setIncidentForm({...incidentForm, location: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" placeholder="e.g., KM 5+200, Near bridge" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Description *</label>
              <textarea required value={incidentForm.description} onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={3} placeholder="Describe what happened..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Persons Involved</label>
                <input type="text" value={incidentForm.persons_involved} onChange={(e) => setIncidentForm({...incidentForm, persons_involved: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Injuries (if any)</label>
                <input type="text" value={incidentForm.injuries} onChange={(e) => setIncidentForm({...incidentForm, injuries: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Immediate Action Taken</label>
              <textarea value={incidentForm.immediate_action} onChange={(e) => setIncidentForm({...incidentForm, immediate_action: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setIncidentDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary bg-error hover:bg-error/90">Report Incident</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Checklist Dialog */}
      <Dialog open={checklistDialogOpen} onOpenChange={setChecklistDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">SAFETY CHECKLIST</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChecklistSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Date *</label>
                <input type="date" required value={checklistForm.date} onChange={(e) => setChecklistForm({...checklistForm, date: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Checklist Type *</label>
                <select required value={checklistForm.checklist_type} onChange={(e) => setChecklistForm({...checklistForm, checklist_type: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  {CHECKLIST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="border border-slate-200 rounded-sm">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs uppercase">Check Item</th>
                    <th className="text-center py-2 px-3 text-xs uppercase w-24">Status</th>
                    <th className="text-left py-2 px-3 text-xs uppercase w-40">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistForm.items.map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="py-2 px-3 text-sm">{item.item}</td>
                      <td className="py-2 px-3">
                        <select value={item.status} onChange={(e) => updateChecklistItem(idx, 'status', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm">
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="NA">N/A</option>
                        </select>
                      </td>
                      <td className="py-2 px-3">
                        <input type="text" value={item.remarks} onChange={(e) => updateChecklistItem(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm" placeholder="Remarks" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Overall Remarks</label>
              <textarea value={checklistForm.overall_remarks} onChange={(e) => setChecklistForm({...checklistForm, overall_remarks: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setChecklistDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Submit Checklist</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PPE Dialog */}
      <Dialog open={ppeDialogOpen} onOpenChange={setPpeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">PPE COMPLIANCE CHECK</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePpeSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Employee *</label>
                <select required value={ppeForm.employee_id} onChange={(e) => setPpeForm({...ppeForm, employee_id: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  <option value="">Select Employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Date *</label>
                <input type="date" required value={ppeForm.date} onChange={(e) => setPpeForm({...ppeForm, date: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'helmet', label: 'Safety Helmet' },
                { key: 'safety_shoes', label: 'Safety Shoes' },
                { key: 'reflective_jacket', label: 'Reflective Jacket' },
                { key: 'gloves', label: 'Safety Gloves' },
                { key: 'safety_glasses', label: 'Safety Glasses' },
                { key: 'ear_plugs', label: 'Ear Plugs' },
                { key: 'harness', label: 'Safety Harness' }
              ].map(item => (
                <label key={item.key} className="flex items-center space-x-2 p-2 bg-slate-50 rounded-sm cursor-pointer hover:bg-slate-100">
                  <input type="checkbox" checked={ppeForm[item.key]} onChange={(e) => setPpeForm({...ppeForm, [item.key]: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Remarks</label>
              <textarea value={ppeForm.remarks} onChange={(e) => setPpeForm({...ppeForm, remarks: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setPpeDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Save Record</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyCompliance;
